import { motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import x from '../../assets/icons/close.svg'
import editPayment from '../../apis/editPayment'
import getCountries from '../../apis/getCountries'
import getPaymentCountries from '../../apis/getPaymentCountries'
import addCountryForPayment from '../../apis/addCountryForPayment'
import deleteCountryFromPayment from '../../apis/deleteCountryFromPayment'
import Select from 'react-select'

const EditPaymnet = ({ payment, refresh, active, close }) => {
  const popupref = useRef(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // Payment method details state
  const [paymentData, setPaymentData] = useState({
    title: payment.title,
    type: payment.type || 'package',
    type_name_en: payment.type_name_en || '',
    type_name_ar: payment.type_name_ar || '',
    is_active: payment.is_active || 1,
  })

  // State for available countries (all), the originally saved allowed countries,
  // and the currently selected allowed countries.
  const [allCountries, setAllCountries] = useState([])
  const [selectedCountries, setSelectedCountries] = useState([])
  const [originalSelectedCountries, setOriginalSelectedCountries] = useState([])

  // Fetch all available countries and allowed countries for this payment
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        // Fetch full country list
        const countries = await getCountries()
        setAllCountries(countries)

        // Fetch allowed countries for this payment.
        // IMPORTANT: Ensure that getPaymentCountries returns country names that match
        // the "name" property in the objects returned by getCountries.
        const allowed = await getPaymentCountries(payment.title)
        const selected = countries
          .filter((country) => allowed.includes(country.name))
          .map((country) => ({
            value: country.id,
            label: country.name,
          }))
        setSelectedCountries(selected)
        setOriginalSelectedCountries(selected)
      } catch (err) {
        console.error('Error fetching countries', err)
      }
    }
    fetchCountries()
  }, [payment.title])

  // Prepare react-select options for available countries
  const countryOptions = allCountries.map((country) => ({
    value: country.id,
    label: country.name,
  }))

  // Handle react-select changes (adds or removes selected countries)
  const handleCountryChange = (selectedOptions) => {
    setSelectedCountries(selectedOptions || [])
  }

  // Handle changes for payment details fields
  const handleChange = (e) => {
    const { name, value } = e.target
    setPaymentData((prev) => ({
      ...prev,
      [name]: value || null,
    }))
  }

  // Submit handler: update payment details and update allowed countries.
  const handelAddCategory = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // First, update payment details.
      await editPayment(paymentData, payment.id)

      // Determine which countries are new (i.e. added) by comparing current selection with original.
      const added = selectedCountries.filter(
        (sc) => !originalSelectedCountries.some((os) => os.value === sc.value)
      )
      // Determine removed countries similarly.
      const removed = originalSelectedCountries.filter(
        (os) => !selectedCountries.some((sc) => os.value === sc.value)
      )

      // Only call the add API if there are new countries.
      if (added.length > 0) {
        // Build payload with only the new (not already assigned) country IDs.
        const payload = {
          Payment: payment.title,
          idCountry: JSON.stringify(added.map((c) => c.value)),
        }
        // Call the assign API (make sure the endpoint is correct)
        await addCountryForPayment(payload)
      }

      // Only call the delete API if there are any removed countries.
      if (removed.length > 0) {
        const payload = {
          Payment: payment.title,
          idCountry: JSON.stringify(removed.map((c) => c.value)),
        }
        await deleteCountryFromPayment(payload)
      }

      refresh()
      close()
    } catch (err) {
      setError('Error editing payment method')
      console.error('Error editing payment method:', err)
    } finally {
      setLoading(false)
    }
  }

  // Close modal if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupref.current && !popupref.current.contains(event.target)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [close])

  // Lock body scroll when modal is active
  useEffect(() => {
    document.body.style.overflow = active ? 'hidden' : 'auto'
  }, [active])

  return (
    <motion.div
      className="fixed inset-0 bg-overlay/30 w-screen z-40 h-screen grid place-items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        ref={popupref}
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ duration: 0.3 }}
        className="w-1/3 min-h-10 bg-white rounded-lg flex flex-col justify-center items-center"
      >
        <form onSubmit={handelAddCategory} className="w-full">
          <div className="w-full px-8 py-5 flex flex-col gap-8">
            <div className="flex border-b-[1px] pb-4 justify-between border-b-darkBlue w-full">
              <div>Edit {payment.title} Payment Method</div>
              <img
                src={x}
                onClick={close}
                className="cursor-pointer"
                alt="Close"
              />
            </div>

            {/* Type Selection Dropdown */}
            <div className="w-full rounded-xl p-4 border-[1px] border-b-darkBlue">
              <select
                name="type"
                value={paymentData.type}
                onChange={handleChange}
                className="w-full focus:outline-none bg-transparent"
                required
              >
                <option value="package">Package</option>
                <option value="balance">Balance</option>
              </select>
            </div>

            {/* Additional Fields */}
            {[
              { name: 'type_name_en', placeholder: 'Type Name (English)' },
              { name: 'type_name_ar', placeholder: 'Type Name (Arabic)' },
            ].map((field) => (
              <div
                key={field.name}
                className="w-full rounded-xl p-4 border-[1px] border-b-darkBlue"
              >
                <input
                  name={field.name}
                  value={paymentData[field.name] || ''}
                  onChange={handleChange}
                  className="w-full focus:outline-none"
                  placeholder={field.placeholder}
                />
              </div>
            ))}

            {/* Countries Multi-select with Search */}
            <div className="w-full">
              <label className="block mb-2 font-semibold">
                Allowed Countries
              </label>
              <Select
                isMulti
                name="countries"
                options={countryOptions}
                value={selectedCountries}
                onChange={handleCountryChange}
                placeholder="Search and select countries..."
                className="basic-multi-select"
                classNamePrefix="select"
              />
            </div>
          </div>
          {error && (
            <div className="w-full flex justify-center py-3 items-center">
              <span className="bg-red/20 px-3 py-1 text-red rounded">
                {error}
              </span>
            </div>
          )}

          <button
            disabled={loading}
            className="disabled:bg-darkBlue/50 bg-blueButton rounded-b-lg text-white text-center p-4 w-full"
          >
            {loading ? 'Editing...' : 'Edit'}
          </button>
        </form>
      </motion.div>
    </motion.div>
  )
}

export default EditPaymnet
